import React, { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useParams, useNavigate, Link } from 'react-router-dom'
import { useAPI } from '@hooks/useAPI'
import { getProductDetail } from '@services/api/products'
import { formatDate } from '@/utils/date'
import ProductCard from '@components/business/ProductCard'
import Image from '@components/ui/Image'
import Loading from '@components/ui/Loading'
import Button from '@components/ui/Button'
import { getFullAttachmentUrl } from '@services/api/index.js'
import { Document, Page, pdfjs } from 'react-pdf'; // PDF 预览组件
pdfjs.GlobalWorkerOptions.workerSrc = `//cdnjs.cloudflare.com/ajax/libs/pdf.js/${pdfjs.version}/pdf.worker.min.js`;
const ProductDetail = () => {
  const { t, i18n } = useTranslation()
  const { id } = useParams()
  const navigate = useNavigate()
  
  const [showImageModal, setShowImageModal] = useState(false)
  const [showPreviewModal, setShowPreviewModal] = useState(false);
const [previewUrl, setPreviewUrl] = useState('');
const [previewType, setPreviewType] = useState(''); // 标记文件类型：pdf/video/other
const [numPages, setNumPages] = useState(null); // PDF 总页数
const [currentAttachment,setCurrentAttachment] = useState(null);
const [pdfLoading, setPdfLoading] = useState(false); // PDF加载状态
  // API调用
  const { 
    data: productData, 
    loading, 
    error,
    refetch 
  } = useAPI(() => getProductDetail(id, i18n.language), [id, i18n.language])

  const fileurl = getFullAttachmentUrl(productData?.coverImage)

  const handleBackToList = () => {
    navigate(-1)
  }

  const handleProductClick = (productId) => {
    navigate(`/products/${productId}`)
  }

  const handleImageClick = () => {
    setShowImageModal(true)
  }
  

  // PDF加载成功回调（获取总页数）
  const onDocumentLoadSuccess = ({ numPages }) => {
    setNumPages(numPages);
        setPdfLoading(false); // PDF加载完成，隐藏加载状态
  };

// 处理第一个附件预览（核心逻辑，仅取第一个）
  const handleFirstAttachmentClick = () => {
    // 优先取files数组第一个，无则取attachments数组第一个
    const firstAttachment = productData.files?.[0] || productData.attachments?.[0];
    if (!firstAttachment) return;
    const attachmentUrl = getFullAttachmentUrl(firstAttachment.objectName);
    const fileType = firstAttachment.format || '';
    const lowerType = fileType.toLowerCase();

    // 设置预览状态
    setPreviewUrl(attachmentUrl);
    setCurrentAttachment(firstAttachment);
    // 分类标记文件类型
    if (lowerType === '.pdf') {
      setPreviewType('.pdf');
      setPdfLoading(true)
    } else if (['.video', '.mp4', '.avi', '.mov', '.webm', '.mkv'].includes(lowerType)) {
      setPreviewType('video');
    } else {
      setPreviewType('other');
    }
    setShowPreviewModal(true);
  };

  const getCategoryIcon = (category) => {
    const icons = {
      'books': '📚',
      'audiobooks': '🎧', 
      'ebooks': '📱',
      'animation': '🎬',
      'cultural': '🎨'
    }
    return icons[category] || '📋'
  }

  const getAttachmentIcon = (type) => {
    const icons = {
      'pdf': '📄',
      'doc': '📄',
      'audio': '🎧',
      'video': '📹',
      'image': '🖼️'
    }
    return icons[type] || '📎'
  }

  if (loading) {
    return (
        <div className="container section-padding">
          <div className="flex justify-center items-center min-h-[400px]">
            <Loading />
          </div>
        </div>
    )
  }

  if (error) {
    return (
        <div className="container section-padding">
          <div className="text-center py-12">
            <div className="text-red-600 mb-4">
              <svg className="w-16 h-16 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <p className="text-lg font-medium">{t('productDetail.states.loadFailed')}</p>
              <p className="text-sm text-gray-500 mt-2">{error || t('productDetail.states.notFound')}</p>
            </div>
            <div className="space-x-4">
              <Button onClick={handleBackToList}>{t('productDetail.actions.backToList')}</Button>
              <Button variant="outline" onClick={() => refetch()}>{t('productDetail.states.retry')}</Button>
            </div>
          </div>
        </div>
    )
  }

  if (!productData) {
    return (
        <div className="container section-padding">
          <div className="text-center py-12">
            <div className="text-gray-400 mb-4">
              <svg className="w-16 h-16 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
              <p className="text-lg font-medium">{t('productDetail.states.noContent')}</p>
            </div>
            <div className="space-x-4">
              <Button onClick={handleBackToList}>{t('productDetail.actions.backToList')}</Button>
            </div>
          </div>
        </div>
    )
  }

  const { product, relatedProducts = [], sameAuthorProducts = [] } = productData
// 检查是否有第一个附件（控制按钮显示）
  const hasFirstAttachment = !!productData.files?.[0] || !!productData.attachments?.[0]

  return (
      <div className="product-detail-page">
        {/* 面包屑导航 */}
        <div className="bg-gray-50 py-4">
          <div className="container section-padding">
            <div className="flex justify-between items-center">
              <nav className="text-sm text-gray-600">
                <Link to="/" className="hover:text-primary-600">{t('productDetail.breadcrumb.home')}</Link>
                <span className="mx-2">&gt;</span>
                <Link to="/products" className="hover:text-primary-600">{t('productDetail.breadcrumb.products')}</Link>
                <span className="mx-2">&gt;</span>
                <span className="text-gray-900 font-medium">{productData.categoryName}</span>
                <span className="mx-2">&gt;</span>
                <span className="text-gray-900 font-medium truncate max-w-[200px]">
                  {productData.productName}
                </span>
              </nav>
              <Button 
                variant="outline" 
                size="sm" 
                onClick={handleBackToList}
                className="flex items-center gap-2"
              >
                ◀ {t('productDetail.actions.backToList')}
              </Button>
            </div>
          </div>
        </div>

        <div className="container section-padding">
          {/* 产品头部信息 */}
          <div className="product-header bg-white rounded-lg shadow-sm border p-6 mb-8">
            <div className="flex flex-col md:flex-row gap-6 items-start">
              {/* 产品封面 */}
              <div className="shrink-0">
                <div className="product-cover-container max-w-xs">
                  {fileurl ? (
                    <img
                      src={fileurl}
                      alt={productData.productName}
                      className="w-full max-h-80 object-contain bg-gray-100 rounded-lg shadow-md cursor-pointer"
                      onClick={handleImageClick}
                      onError={(e) => { e.target.src = '/images/placeholder-book.png' }}
                    />
                  ) : (
                    <img
                      src="/images/placeholder-book.png"
                      alt={productData.productName}
                      className="w-full max-h-80 object-contain bg-gray-100 rounded-lg shadow-md"
                    />
                  )}
                  <button
                    onClick={handleImageClick}
                    className="mt-4 w-full text-center text-sm text-primary-600 hover:text-primary-700"
                  >
                    {t('productDetail.actions.viewLargeImage')}
                  </button>
                </div>
              </div>

              {/* 产品信息 */}
              <div className="flex-1 space-y-6">
                {/* 标题和基本信息 */}
                <div>
                  <h1 className="text-3xl font-bold text-gray-900 mb-4">
                    {productData.productName}
                  </h1>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm text-gray-600">
                    {productData.author && (
                      <div className="flex items-center">
                        <span className="mr-2">{t('productDetail.info.author')}</span>
                        <span className="font-medium text-gray-900">{productData.author}</span>
                      </div>
                    )}
                    
                    {productData.publisher && (
                      <div className="flex items-center">
                        <span className="mr-2">{t('productDetail.info.publisher')}</span>
                        <span className="font-medium text-gray-900">{productData.publisher}</span>
                      </div>
                    )}
                    
                    {productData.isbn && (
                      <div className="flex items-center">
                        <span className="mr-2">{t('productDetail.info.isbn')}</span>
                        <span className="font-medium text-gray-900">{productData.isbn}</span>
                      </div>
                    )}
                    
                    <div className="flex items-center">
                      <span className="mr-2">{t('productDetail.info.category')}</span>
                      <span className="font-medium text-gray-900">
                        {productData.typeName}
                        {productData.subCategoryName && ` > ${productData.subCategoryName}`}
                      </span>
                    </div>
                    

                  </div>
                </div>

                {/* 产品摘要 */}
                {productData.summary && (
                  <div className="bg-gray-50 rounded-lg p-4">
                    <h3 className="text-lg font-semibold text-gray-900 mb-2">
                      {t('productDetail.sections.summary')}
                    </h3>
                    <p className="text-gray-700 leading-relaxed">{productData.summary}</p>
                  </div>
                )}

                {/* 操作按钮 */}
                <div className="flex flex-wrap gap-4">
                  {productData.files && productData.files.length > 0 && (
                    
                    <Button variant="outline" className="flex items-center gap-2" onClick={handleFirstAttachmentClick}>
                      {t('productDetail.actions.previewContent')} ({productData.files.length})
                    </Button>
                  )}
                  {/* <Button variant="outline" className="flex items-center gap-2">
                    {t('productDetail.actions.copyrightInfo')}
                  </Button>
                  <Button variant="outline" className="flex items-center gap-2">
                    {t('productDetail.actions.favorite')}
                  </Button> */}
                </div>
              </div>
            </div>
          </div>

          {/* 详细信息区 */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {/* 主要内容 */}
            <div className="lg:col-span-2 space-y-8">
              {/* 产品详细描述 */}
              {productData?.remark && (
                <div className="bg-white rounded-lg shadow-sm border p-6">
                  <h2 className="text-xl font-semibold text-gray-900 mb-4">
                    {t('productDetail.sections.description')}
                  </h2>
                  <div className="prose prose-gray max-w-none">
                    <p className="text-gray-700 leading-relaxed whitespace-pre-line">
                      {productData.remark}
                    </p>
                  </div>
                </div>
              )}


              {/* 附件列表 */}
              {productData.attachments && productData.attachments.length > 0 && (
                <div className="bg-white rounded-lg shadow-sm border p-6">
                  <h2 className="text-xl font-semibold text-gray-900 mb-4">
                    {t('productDetail.sections.attachments')} ({t('productDetail.sections.attachmentCount', { count: productData.attachments.length })})
                  </h2>
                  <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                    {productData.attachments.map((attachment, index) => (
                      <div
                        key={index}
                        className="attachment-item bg-gray-50 rounded-lg p-4 text-center cursor-pointer
                                 hover:bg-gray-100 transition-colors duration-200"
                        onClick={() => handleAttachmentClick(attachment)}
                      >
                        <div className="attachment-name text-sm font-medium text-gray-900 line-clamp-2 mb-1">
                          {attachment.name}
                        </div>
                        <div className="attachment-meta text-xs text-gray-500">
                          {attachment.size && `${attachment.size} · `}
                          {attachment.type.toUpperCase()}
                        </div>
                      </div>
                    ))}
                  </div>
                  <div className="mt-4 text-sm text-gray-500 text-center">
                    * {t('productDetail.attachments.notice')}
                  </div>
                </div>
              )}
            </div> 

            {/* 信息侧栏 */}
            <div className="lg:col-span-1 space-y-6">


              {/* 版权信息 */}
              {productData.copyright && (
                <div className="bg-white rounded-lg shadow-sm border p-6">
                  <h3 className="text-lg font-semibold text-gray-900 mb-4">
                    {t('productDetail.sections.copyright')}
                  </h3>
                  <div className="space-y-3 text-sm">
                    {productData.copyright.owner && (
                      <div>
                        <span className="text-gray-600">{t('productDetail.copyright.owner')}</span>
                        <p className="text-gray-900 font-medium mt-1">{productData.copyright.owner}</p>
                      </div>
                    )}
                    {productData.copyright.status && (
                      <div>
                        <span className="text-gray-600">{t('productDetail.copyright.status')}</span>
                        <p className="text-gray-900 font-medium mt-1">{productData.copyright.status}</p>
                      </div>
                    )}
                    {productData.copyright.scope && (
                      <div>
                        <span className="text-gray-600">{t('productDetail.copyright.scope')}</span>
                        <p className="text-gray-900 font-medium mt-1">{productData.copyright.scope}</p>
                      </div>
                    )}
                    {productData.copyright.validity && (
                      <div>
                        <span className="text-gray-600">{t('productDetail.copyright.validity')}</span>
                        <p className="text-gray-900 font-medium mt-1">{productData.copyright.validity}</p>
                      </div>
                    )}
                  </div>
                </div>
              )}

            </div>
          </div>
        </div>

        {/* 图片大图模态框 */}
        {showImageModal && (
          <div 
            className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50 p-4"
            onClick={() => setShowImageModal(false)}
          >
            <div className="max-w-4xl">
              <img
                src={fileurl || '/images/placeholder-book.png'}
                alt={productData.productName}
                className="max-w-full max-h-[85vh] object-contain"
              />
            </div>
            <button
              onClick={() => setShowImageModal(false)}
              className="absolute top-4 right-4 text-white hover:text-gray-300 text-2xl"
            >
              ✕
            </button>
          </div>
        )}

{/* 文件预览模态框（核心新增） */}
     {/* 文件预览模态框（仅针对第一个附件） */}
        {showPreviewModal && (
          <div 
            className="fixed inset-0 bg-black bg-opacity-80 flex items-center justify-center z-50 p-4"
            onClick={() => setShowPreviewModal(false)}
          >
            {/* 预览内容容器（点击内部不关闭模态框） */}
            <div className="max-w-5xl max-h-[90vh] w-full bg-white rounded-lg overflow-hidden" onClick={(e) => e.stopPropagation()}>
              {/* 预览头部（文件名+关闭按钮） */}
              <div className="bg-gray-100 p-3 flex justify-between items-center border-b">
               
                <button
                  onClick={() => setShowPreviewModal(false)}
                  className="text-gray-500 hover:text-gray-700 text-lg"
                >
                  ✕
                </button>
              </div>

              {/* 预览内容区（按类型渲染） */}
              <div className="p-4 h-[calc(90vh-60px)] overflow-auto">
                {/* PDF 预览 */}
                {previewType === '.pdf' && (
                  <div className="flex flex-col items-center">
                    {pdfLoading && (
                      <div className="flex flex-col items-center justify-center py-12">
                        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mb-4"></div>
                        <p className="text-gray-600">{t('productDetail.preview.loadingPDF')}</p>
                      </div>
                    )}
                    <Document
                     file={previewUrl}
                      onLoadSuccess={onDocumentLoadSuccess}
                      className="w-full max-w-3xl"
                      loading={null} // 我们自定义加载状态
                      error={
                        <div className="text-center py-8 text-red-600">
                          <p className="mb-2">{t('productDetail.preview.loadFailed')}</p>
                          <a 
                            href={previewUrl} 
                            target="_blank" 
                            rel="noreferrer"
                            className="text-primary-600 hover:underline"
                          >
                            {t('productDetail.preview.openInNewTab')}
                          </a>
                        </div>
                      }>
                      {numPages ? (
                        // 支持多页PDF，渲染所有页面
                        Array.from(new Array(numPages), (_, i) => (
                          <Page key={`page-${i + 1}`} pageNumber={i + 1} scale={1.1} />
                        ))
                      ) : (
                        <div className="text-center py-8 text-gray-600">{t('productDetail.preview.loadingPDF')}</div>
                      )}
                    </Document>
                    {/* PDF 页码提示 */}
                    {numPages && (
                      <div className="mt-4 text-sm text-gray-600">
                        {t('productDetail.preview.totalPages', { count: numPages })}
                      </div>
                    )}
                  </div>
                )}

                {/* 视频预览 */}
                {previewType === 'video' && (
                  <div className="flex justify-center">
                    <video
                      src={previewUrl}
                      controls
                      className="w-full max-w-3xl max-h-[70vh] object-contain"
                      preload="metadata"
                      poster={productData.coverImage ? fileurl : ''}
                    >
                      <p className="text-gray-600">{t('productDetail.preview.videoNotSupported')}</p>
                      <a href={previewUrl} target="_blank" rel="noreferrer" className="text-primary-600 hover:underline">
                        {t('productDetail.preview.openInNewTab')}
                      </a>
                    </video>
                  </div>
                )}
                {/* 其他文件预览（Word/Excel/图片等，用iframe兼容） */}
                {previewType === 'other' && (
                  <div className="w-full h-full flex flex-col">
                    <iframe
                      src={previewUrl}
                      className="w-full h-full border-0"
                      title={currentAttachment?.name || 'File Preview'}
                    />
                    {/* 兼容提示：部分文件可能无法预览，提供下载/新窗口打开选项 */}
                    <div className="mt-4 text-center text-sm text-gray-500">
                      <p>{t('productDetail.preview.otherFileTip')}</p>
                      <div className="flex justify-center gap-3 mt-2">
                        <a 
                          href={previewUrl} 
                          target="_blank" 
                          rel="noreferrer"
                          className="text-primary-600 hover:underline"
                        >
                          {t('productDetail.preview.openInNewTab')}
                        </a>
                        <a 
                          href={previewUrl} 
                          download={currentAttachment?.name}
                          className="text-gray-600 hover:underline"
                        >
                          {t('productDetail.preview.download')}
                        </a>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
      </div>
  )
}

export default ProductDetail